/**
 * Routes and waypoints.
 *
 * A route is an ordered list of waypoints. Each pair of consecutive waypoints
 * makes a leg. Per-leg we compute bearing, distance, cumulative distance, and
 * (given a speed) ETA. Route monitoring against a current position uses the
 * nav module's distanceToRun for XTE and DTG on the active leg.
 *
 * GPX is the lingua franca of plotters and Garmin / iSailor / Navionics etc.
 * all read and write it. We support a minimal subset — waypoints (`wpt`) and
 * routes (`rte` containing `rtept`) — which is enough for round-tripping the
 * kinds of routes RYA students build.
 */
import {
  courseToSteer,
  distanceToRun,
  formatBearing,
  formatDistance,
  rhumbBearingDeg,
  rhumbDistanceNm,
  type DtrResult,
} from '@/nav';
import { streamAtPosition } from '@/tides';
import { trackSegments, type Track } from '@/ownship';

export interface Waypoint {
  id: string;
  /** [lon, lat] WGS84 degrees. */
  position: [number, number];
  /** Short label shown on the chart, e.g. "WP1" or "Cowes". */
  name?: string;
  /** Free-text note carried in GPX <desc>. */
  description?: string;
  /** Map icon image id (icons.ts), e.g. 'plot-waypoint'. Defaults to square. */
  icon?: string;
  /** Proximity/arrival alarm radius (nm). Undefined = no alarm on this point. */
  arrivalRadiusNm?: number;
  /**
   * Explicit per-waypoint arrival-alarm switch. `true` arms the alarm (using this
   * point's radius, else the route-wide one, else a default), `false` silences it
   * even if a radius is set, and `undefined` leaves the old behaviour where a set
   * radius means the alarm is on. Kept separate from the radius so we can hold a
   * radius while the alarm is off.
   */
  arrivalAlarm?: boolean;
}

export interface Route {
  id: string;
  name: string;
  waypoints: Waypoint[];
  /** Arrival/proximity alarm radius applied to every waypoint (nm), unless a
   * waypoint sets its own. Undefined = no route-wide proximity alarm. */
  arrivalRadiusNm?: number;
  /** Default cross-track-distance alarm for the route (nm). Undefined = off. */
  xtdNm?: number;
  /** Per-leg XTD overrides, keyed by the leg's start waypoint id. */
  legXtd?: Record<string, number>;
  /** Per-leg free-text notes, keyed by the leg's start waypoint id. Mirrors legXtd. */
  legNotes?: Record<string, string>;
  /** Free-text skipper's note on the route (pilotage reminders, hazards). */
  notes?: string;
  /**
   * The colour the route's line is plotted in on the chart, as a CSS colour
   * string (usually a hex like '#e53935'). Undefined falls back to the app's
   * default route colour, so old saved routes and freshly-built ones look exactly
   * as before until we pick a colour — handy for telling several routes apart. */
  color?: string;
  /** When the route was first created (epoch ms). Stamped on creation and kept
   *  for the life of the route, so the routes list can show when we built it.
   *  Optional because routes saved by an older build won't carry one. */
  createdAt?: number;
}

/** Sensible arrival radius when the alarm is switched on but no radius is set (nm). */
export const DEFAULT_ARRIVAL_RADIUS_NM = 0.1;

/** Effective cross-track limit for the leg starting at `fromId` (nm), or null. */
export function effectiveLegXtd(route: Route, fromId: string): number | null {
  const perLeg = route.legXtd?.[fromId];
  if (typeof perLeg === 'number') return perLeg;
  if (typeof route.xtdNm === 'number') return route.xtdNm;
  return null;
}

/** Effective proximity radius for a waypoint in a route (nm), or null. */
export function effectiveWaypointRadius(route: Route, wp: Waypoint): number | null {
  // An explicit per-waypoint switch wins. Off silences the point even if it (or
  // the route) carries a radius; on arms it, preferring this point's radius, then
  // the route-wide one, then a default so an armed alarm is never silently zero.
  if (wp.arrivalAlarm === false) return null;
  if (wp.arrivalAlarm === true)
    return wp.arrivalRadiusNm ?? route.arrivalRadiusNm ?? DEFAULT_ARRIVAL_RADIUS_NM;
  if (typeof wp.arrivalRadiusNm === 'number') return wp.arrivalRadiusNm;
  if (typeof route.arrivalRadiusNm === 'number') return route.arrivalRadiusNm;
  return null;
}

/**
 * Cross-track distance (nm, unsigned) from a point to the leg a->b. Used to
 * turn an XTD-corridor handle drag into a cross-track limit.
 */
export function legCrossTrackNm(
  a: [number, number],
  b: [number, number],
  point: [number, number],
): number {
  return Math.abs(distanceToRun(point, a, b).xteNm);
}

export interface LegInfo {
  fromId: string;
  toId: string;
  /** Bearing along the leg in degrees true. */
  bearingDeg: number;
  /** Leg length in nm. */
  distanceNm: number;
  /** Cumulative distance from the start of the route to the end of THIS leg. */
  cumulativeNm: number;
  /** ETA at the leg end, in hours from route start, given a passage speed. */
  etaHours?: number;
  /**
   * STW mode only: the effective speed made good along the leg once the tidal
   * stream is folded in, knots. This is SIGNED — a foul tide stronger than the
   * boat gives a value at or below zero (no forward progress), which the timing
   * clamps and flags rather than turning into a nonsense ETA. Undefined in SOG
   * mode (there's no tide to fold in).
   */
  smgKn?: number;
  /** STW mode: true when a tidal stream was actually folded into this leg. */
  tideApplied?: boolean;
  /**
   * STW mode: true when no stream data was available for this leg, so we fell
   * back to treating the STW as an SOG for it (leg time = distance / STW) and
   * say so rather than inventing a stream.
   */
  noStreamData?: boolean;
  /**
   * STW mode: true when the tide made the leg unachievable — either too strong
   * across the track to hold it, or a foul tide at/above the boat's forward
   * speed. The ETA is clamped to a crawl and this flags why.
   */
  unachievable?: boolean;
}

export interface RouteSummary {
  legs: LegInfo[];
  /** Total route distance in nm. */
  totalNm: number;
  /** Total ETA in hours, when a speed is given. */
  totalHours?: number;
}

export function summariseRoute(route: Route, speedKnots?: number): RouteSummary {
  const legs: LegInfo[] = [];
  let cumulative = 0;
  let cumulativeHours = 0;
  for (let i = 1; i < route.waypoints.length; i++) {
    const a = route.waypoints[i - 1];
    const b = route.waypoints[i];
    const bearingDeg = rhumbBearingDeg(a.position, b.position);
    const distanceNm = rhumbDistanceNm(a.position, b.position);
    cumulative += distanceNm;
    let etaHours: number | undefined;
    if (speedKnots && speedKnots > 0) {
      cumulativeHours += distanceNm / speedKnots;
      etaHours = cumulativeHours;
    }
    legs.push({
      fromId: a.id,
      toId: b.id,
      bearingDeg,
      distanceNm,
      cumulativeNm: cumulative,
      etaHours,
    });
  }
  return {
    legs,
    totalNm: cumulative,
    totalHours: speedKnots && speedKnots > 0 ? cumulativeHours : undefined,
  };
}

// -----------------------------------------------------------------------------
// Expected speed + tide-aware (STW) leg timing
// -----------------------------------------------------------------------------

/**
 * The expected passage speed the skipper sets, as a LABELLED choice rather than a
 * bare number:
 *   - 'sog' (speed over the ground) is already the speed made good, so the tide
 *     is baked in — leg time is just distance / SOG.
 *   - 'stw' (speed through the water) is the boat's speed relative to the water,
 *     so the tide has to be folded in per leg to get the real time on the ground.
 */
export type SpeedRef = 'sog' | 'stw';

export interface PlanSpeed {
  ref: SpeedRef;
  /** The speed value in knots (interpreted as SOG or STW per `ref`). */
  knots: number;
}

/**
 * Stream at a position and instant: set (deg true the tide flows TOWARDS) and
 * rate (kn), or null when we have no stream data there. Same shape the CTS and
 * passage tools use (see streamAtPosition), with null added so a leg with no data
 * can be handled honestly instead of inventing a stream.
 */
export type LegStreamFn = (
  position: [number, number],
  atMs: number,
) => { setDeg: number; rateKn: number } | null;

/**
 * Crawl floor for the effective speed made good, knots. When a foul or heavily
 * cross-setting tide leaves the boat with no forward progress, we clamp the SMG
 * to this so the ETA is huge-but-finite (and flagged) rather than infinite,
 * negative, or NaN. Matches the crawl the passage simulator uses.
 */
export const SMG_CRAWL_KN = 0.05;

export interface StwLegSpeed {
  /** SMG used for timing, knots — always ≥ SMG_CRAWL_KN (clamped when foul). */
  smgKn: number;
  /**
   * The true SIGNED speed made good along the leg, knots, before clamping. This
   * can be zero or negative when a foul tide meets or beats the boat's forward
   * speed. Kept separate from `smgKn` so callers can show "no progress" honestly
   * while the timing still uses a finite crawl.
   */
  rawSmgKn: number;
  /** True when the tide made the leg unachievable (clamped): cross-set too strong
   *  to hold the track, or foul tide at/above the boat's forward capability. */
  unachievable: boolean;
}

/**
 * Effective speed made good along one leg for a boat holding the ground track at
 * a speed THROUGH THE WATER of `stwKn`, against a tidal stream (set/rate).
 *
 * We REUSE the tested CTS triangle (courseToSteer) to solve the heading and to
 * flag the case where the cross-set is too strong to hold the track at all. But
 * courseToSteer returns its speed over ground as a magnitude, and a directly foul
 * tide stronger than the boat makes NEGATIVE progress that the magnitude hides.
 * So we resolve the tidal vector onto the leg and take the SIGNED along-track
 * speed made good:
 *
 *   along-track tide  t∥ = rate · cos(set − legBearing)   (+ with us, − foul)
 *   cross-track tide  t⊥ = rate · sin(set − legBearing)
 *   boat's along-track water speed once it has spent enough of its STW cancelling
 *   the cross-set:    b∥ = √(STW² − t⊥²)                   (real only if |t⊥| ≤ STW)
 *   speed made good   SMG = b∥ + t∥                        (signed)
 *
 * |SMG| equals courseToSteer's sogKnots for the achievable, forward case; the sign
 * is what we add. See docs/ardent_chart_plotter_brief.md §6.3.
 */
export function stwLegSmg(
  from: [number, number],
  to: [number, number],
  stwKn: number,
  setDeg: number,
  rateKn: number,
  leewayDeg = 0,
): StwLegSpeed {
  const cts = courseToSteer({
    from,
    to,
    tidalSet: setDeg,
    tidalRate: rateKn,
    boatSpeed: stwKn,
    leewayDeg,
  });
  // Cross-set too strong to hold the track: courseToSteer already flags this
  // (sin β > 1). Honour its verdict so the route and the CTS tool agree.
  if (cts.unachievable) {
    return { smgKn: SMG_CRAWL_KN, rawSmgKn: NaN, unachievable: true };
  }
  const rel = ((setDeg - cts.groundTrackDeg) * Math.PI) / 180;
  const tAlong = rateKn * Math.cos(rel);
  const tCross = rateKn * Math.sin(rel);
  // √ guards the same |t⊥| ≤ STW condition courseToSteer checks; max(0,…) keeps
  // us safe against floating-point dust right at the limit.
  const boatAlong = Math.sqrt(Math.max(0, stwKn * stwKn - tCross * tCross));
  const smg = boatAlong + tAlong;
  if (!(smg > SMG_CRAWL_KN)) {
    // Foul tide at/above the boat's forward capability: no useful progress.
    return { smgKn: SMG_CRAWL_KN, rawSmgKn: smg, unachievable: true };
  }
  return { smgKn: smg, rawSmgKn: smg, unachievable: false };
}

export interface TimedRouteSummary extends RouteSummary {
  /** Whether the ETAs are the flat SOG calc or the tide-folded STW calc. */
  basis: 'sog' | 'stw-tide';
  /** STW mode: true if any leg had no stream data and fell back to SOG. */
  anyNoStreamData: boolean;
  /** STW mode: true if any leg was unachievable at the chosen departure. */
  anyUnachievable: boolean;
}

const MS_PER_HOUR = 3_600_000;

/**
 * Summarise a route with expected-speed-aware, tide-aware leg timing.
 *
 * SOG mode is the old flat behaviour, just relabelled: leg time = distance / SOG,
 * no tide folded in (SOG already IS the over-ground speed).
 *
 * STW mode walks the legs IN SEQUENCE, carrying the accumulated ETA so each leg
 * reads the tide at the clock time the boat actually reaches it (progressive, not
 * all at the departure instant), then times the leg by its tide-folded speed made
 * good (stwLegSmg). A leg with no stream data falls back to STW-as-SOG and is
 * flagged; a foul/cross tide that stalls the boat is clamped and flagged.
 *
 * `opts.streamFn` defaults to the chart-diamond lookup the CTS and passage tools
 * use, so there is one tidal model across the app; tests inject a fixed field.
 */
export function summariseRouteTimed(
  route: Route,
  speed: PlanSpeed,
  opts: { departMs?: number; streamFn?: LegStreamFn; leewayDeg?: number } = {},
): TimedRouteSummary {
  const knots = speed.knots;
  const hasSpeed = knots > 0;
  const streamFn: LegStreamFn = opts.streamFn ?? streamAtPosition;
  const departMs = opts.departMs ?? 0;
  const leewayDeg = opts.leewayDeg ?? 0;

  const legs: LegInfo[] = [];
  let cumulative = 0;
  let cumulativeHours = 0;
  let anyNoStreamData = false;
  let anyUnachievable = false;

  for (let i = 1; i < route.waypoints.length; i++) {
    const a = route.waypoints[i - 1];
    const b = route.waypoints[i];
    const bearingDeg = rhumbBearingDeg(a.position, b.position);
    const distanceNm = rhumbDistanceNm(a.position, b.position);
    cumulative += distanceNm;

    const leg: LegInfo = {
      fromId: a.id,
      toId: b.id,
      bearingDeg,
      distanceNm,
      cumulativeNm: cumulative,
    };

    if (hasSpeed) {
      if (speed.ref === 'sog') {
        cumulativeHours += distanceNm / knots;
        leg.etaHours = cumulativeHours;
      } else {
        // The time the boat reaches the START of this leg is the ETA so far, so
        // read the stream there and then. Sample at the leg start position.
        const atMs = departMs + cumulativeHours * MS_PER_HOUR;
        const sample = streamFn(a.position, atMs);
        if (!sample) {
          // No data here — treat STW as SOG for this leg, and say so.
          cumulativeHours += distanceNm / knots;
          leg.etaHours = cumulativeHours;
          leg.smgKn = knots;
          leg.noStreamData = true;
          anyNoStreamData = true;
        } else {
          const s = stwLegSmg(a.position, b.position, knots, sample.setDeg, sample.rateKn, leewayDeg);
          cumulativeHours += distanceNm / s.smgKn;
          leg.etaHours = cumulativeHours;
          leg.smgKn = s.rawSmgKn;
          leg.tideApplied = true;
          if (s.unachievable) {
            leg.unachievable = true;
            anyUnachievable = true;
          }
        }
      }
    }

    legs.push(leg);
  }

  return {
    legs,
    totalNm: cumulative,
    totalHours: hasSpeed ? cumulativeHours : undefined,
    basis: speed.ref === 'stw' ? 'stw-tide' : 'sog',
    anyNoStreamData,
    anyUnachievable,
  };
}

/**
 * Format a leg into a paper-style label, e.g.
 *   "Leg 1: 095°T  4.62 nm  ETA +0:46"
 */
export function describeLeg(leg: LegInfo, idx: number): string {
  const parts = [
    `Leg ${idx + 1}:`,
    formatBearing(leg.bearingDeg),
    formatDistance(leg.distanceNm),
  ];
  if (leg.etaHours !== undefined) {
    const h = Math.floor(leg.etaHours);
    const m = Math.round((leg.etaHours - h) * 60);
    parts.push(`ETA +${h}:${String(m).padStart(2, '0')}`);
  }
  return parts.join('  ');
}

// -----------------------------------------------------------------------------
// Route monitoring
// -----------------------------------------------------------------------------

export interface RouteMonitor {
  /** Index of the active leg (0-based). null = before route start. */
  activeLegIndex: number | null;
  /** XTE + DTG for the active leg, from the nav module. */
  active?: DtrResult;
  /** Bearing along the active leg, for the steering display. */
  legBearingDeg?: number;
  /** Cumulative distance from current position to the route end, in nm. */
  remainingNm?: number;
}

/**
 * Decide which leg the boat is "on" by picking the leg with the smallest
 * positive distance-to-go. If the boat is past the end of the route, the
 * active leg is the last leg.
 */
export function monitorRoute(
  route: Route,
  position: [number, number],
  minLegIndex = 0,
): RouteMonitor {
  if (route.waypoints.length < 2) return { activeLegIndex: null };

  const legCount = route.waypoints.length - 1;
  const legDistances: number[] = [];
  for (let i = 0; i < legCount; i++) {
    legDistances.push(
      rhumbDistanceNm(route.waypoints[i].position, route.waypoints[i + 1].position),
    );
  }

  // Per leg: compute DTR plus where the foot of perpendicular sits along the
  // leg (in nm from the leg start). We pick the active leg by these rules:
  //   1. The first leg whose XTE-foot is strictly INSIDE the leg corridor
  //      (along ∈ [0, legLen)). That's the leg we are still travelling.
  //   2. If no leg matches that (all feet are past the leg end), we've
  //      finished — anchor to the last leg with DTG = 0.
  type LegState = {
    idx: number;
    dtr: DtrResult;
    legBearing: number;
    along: number;
    legLen: number;
  };
  const states: LegState[] = [];
  for (let i = 0; i < legCount; i++) {
    const a = route.waypoints[i].position;
    const b = route.waypoints[i + 1].position;
    const dtr = distanceToRun(position, a, b);
    const legLen = legDistances[i];
    const along = legLen - dtr.distanceToGoNm;
    states.push({ idx: i, dtr, legBearing: rhumbBearingDeg(a, b), along, legLen });
  }

  // Find the first leg the boat hasn't passed (foot is short of the end). When
  // following "from waypoint N" we never look at legs before N, so the boat is
  // steered onward from there rather than snapping back to the nearest leg.
  const floor = Math.max(0, Math.min(minLegIndex, legCount - 1));
  let pick = states.slice(floor).find((s) => s.along < s.legLen - 1e-9);
  if (!pick) pick = states[states.length - 1];

  let remaining = pick.dtr.distanceToGoNm;
  for (let j = pick.idx + 1; j < legCount; j++) remaining += legDistances[j];
  if (pick.along >= pick.legLen - 1e-9) remaining = 0; // past the route end

  return {
    activeLegIndex: pick.idx,
    active: pick.dtr,
    legBearingDeg: pick.legBearing,
    remainingNm: remaining,
  };
}

// -----------------------------------------------------------------------------
// GPX
// -----------------------------------------------------------------------------

const GPX_HEADER = '<?xml version="1.0" encoding="UTF-8"?>';

function xmlEscape(s: string): string {
  return s.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Serialise a route into a GPX 1.1 string. */
export function exportRouteToGpx(route: Route): string {
  const rtepts = route.waypoints
    .map((w) => {
      const inner = [
        w.name ? `<name>${xmlEscape(w.name)}</name>` : '',
        w.description ? `<desc>${xmlEscape(w.description)}</desc>` : '',
      ]
        .filter(Boolean)
        .join('');
      const tag = inner ? `<rtept lat="${w.position[1]}" lon="${w.position[0]}">${inner}</rtept>` : `<rtept lat="${w.position[1]}" lon="${w.position[0]}"/>`;
      return tag;
    })
    .join('');
  const rteDesc = route.notes ? `\n    <desc>${xmlEscape(route.notes)}</desc>` : '';
  return (
    `${GPX_HEADER}\n` +
    `<gpx version="1.1" creator="Ardent Fix" xmlns="http://www.topografix.com/GPX/1/1">\n` +
    `  <rte>\n    <name>${xmlEscape(route.name)}</name>${rteDesc}\n    ${rtepts}\n  </rte>\n` +
    `</gpx>\n`
  );
}

/**
 * Serialise an own-ship track to GPX 1.1 as a <trk> with one <trkseg> per
 * continuous segment (source-change gaps break segments), each point a <trkpt>
 * with its timestamp. This is the same GPX every plotter reads, so a recorded
 * run drops straight into Navionics / iSailor / OpenCPN etc. A track is just a
 * series of fixes.
 */
export function exportTrackToGpx(track: Track, name = 'Track'): string {
  const segs = trackSegments(track)
    .filter((seg) => seg.length > 0)
    .map((seg) => {
      const pts = seg
        .map(
          (p) =>
            `<trkpt lat="${p.position[1]}" lon="${p.position[0]}">` +
            `<time>${new Date(p.t).toISOString()}</time></trkpt>`,
        )
        .join('');
      return `    <trkseg>${pts}</trkseg>`;
    })
    .join('\n');
  return (
    `${GPX_HEADER}\n` +
    `<gpx version="1.1" creator="Ardent Fix" xmlns="http://www.topografix.com/GPX/1/1">\n` +
    `  <trk>\n    <name>${xmlEscape(name)}</name>\n${segs}\n  </trk>\n` +
    `</gpx>\n`
  );
}

/**
 * Parse a GPX 1.x string and return any routes plus standalone waypoints found.
 *
 * Hand-rolled because we don't want to ship a full XML parser bundle. The
 * parser is tolerant of whitespace, attribute order and namespace prefixes,
 * but does not chase external entities (no DOCTYPE handling).
 */
export function importGpx(xml: string): { routes: Route[]; waypoints: Waypoint[] } {
  const routes: Route[] = [];
  const waypoints: Waypoint[] = [];
  let routeIdx = 0;
  let waypointIdx = 0;

  // Strip prologue and DOCTYPE (we're not supporting external DTDs).
  const cleaned = xml.replace(/<\?xml[^?]*\?>/g, '').replace(/<!DOCTYPE[^>]*>/g, '');

  // Pull standalone waypoints (<wpt lat=".." lon="..">…</wpt>).
  const wptRe = /<wpt\b([^>]*)>([\s\S]*?)<\/wpt>|<wpt\b([^/>]*)\/>/g;
  for (const m of cleaned.matchAll(wptRe)) {
    const attrs = m[1] ?? m[3] ?? '';
    const lat = parseFloat(attrAt(attrs, 'lat') ?? 'NaN');
    const lon = parseFloat(attrAt(attrs, 'lon') ?? 'NaN');
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const inner = m[2] ?? '';
    const name = textOf(inner, 'name');
    const desc = textOf(inner, 'desc');
    waypoints.push({
      id: `wpt-${++waypointIdx}`,
      position: [lon, lat],
      name,
      description: desc,
    });
  }

  // Pull each route.
  const rteRe = /<rte\b[^>]*>([\s\S]*?)<\/rte>/g;
  for (const m of cleaned.matchAll(rteRe)) {
    const body = m[1];
    const name = textOf(body, 'name') ?? `Route ${++routeIdx}`;
    const notes = textOf(body, 'desc');
    const wps: Waypoint[] = [];
    const ptRe = /<rtept\b([^>]*)>([\s\S]*?)<\/rtept>|<rtept\b([^/>]*)\/>/g;
    let ptIdx = 0;
    for (const pm of body.matchAll(ptRe)) {
      const attrs = pm[1] ?? pm[3] ?? '';
      const lat = parseFloat(attrAt(attrs, 'lat') ?? 'NaN');
      const lon = parseFloat(attrAt(attrs, 'lon') ?? 'NaN');
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const inner = pm[2] ?? '';
      wps.push({
        id: `rtept-${++ptIdx}`,
        position: [lon, lat],
        name: textOf(inner, 'name'),
        description: textOf(inner, 'desc'),
      });
    }
    if (wps.length >= 2) {
      routes.push({ id: `rte-${routeIdx}`, name, waypoints: wps, ...(notes ? { notes } : {}) });
    }
  }
  return { routes, waypoints };
}

function attrAt(attrs: string, name: string): string | undefined {
  const m = new RegExp(`${name}\\s*=\\s*"([^"]*)"`).exec(attrs);
  return m ? m[1] : undefined;
}
function textOf(body: string, tag: string): string | undefined {
  const m = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)<\\/${tag}>`).exec(body);
  if (!m) return undefined;
  return m[1]
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .trim() || undefined;
}
